import React, { useState, useRef } from 'react'
import {
  HiOutlineXMark,
  HiOutlineChatBubbleLeft,
  HiOutlineSpeakerWave,
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineCalendar,
  HiOutlineUser,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineExclamationTriangle,
  HiOutlineArrowDownTray
} from 'react-icons/hi2'
import { tokenStorage } from '../../utils/crypto'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import BlockedAction from '../common/BlockedAction'

const MessageViewModal = ({ isOpen, onClose, message }) => {
  const { getEntity } = useWorkspace()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef(null)

  if (!isOpen || !message) return null

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value)
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime
      setCurrentTime(seekTime)
    }
  }

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getSecurityBadge = (level) => {
    const configs = {
      red: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: HiOutlineShieldExclamation,
        label: 'High Security'
      },
      yellow: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: HiOutlineExclamationTriangle,
        label: 'Medium Security'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: HiOutlineShieldCheck,
        label: 'Low Security'
      }
    }

    const config = configs[level] || configs.green
    const Icon = config.icon

    return (
      <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </div>
    )
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDownloadText = async () => {
    if (!message?.secure_download_url) return
    try {
      const token = tokenStorage.getToken()
      if (!token) return
      const response = await fetch(message.secure_download_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      })
      if (!response.ok) return
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = message.file_name || `${message.title || 'message'}.txt`
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading text message:', err)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl transform transition-all">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  message.type === 'voice' ? 'bg-blue-100' : 'bg-green-100'
                }`}>
                  {message.type === 'voice' ? (
                    <HiOutlineSpeakerWave className="w-6 h-6 text-blue-600" />
                  ) : (
                    <HiOutlineChatBubbleLeft className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {message.title || (message.type === 'voice' ? 'Voice Message' : 'Text Message')}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {message.title ? `${message.type === 'voice' ? 'Voice' : 'Text'} message from ` : 'from '}{message.created_by_name}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineXMark className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Message Info */}
              <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <HiOutlineUser className="w-4 h-4" />
                  <span className="font-medium">{message.created_by_name}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <HiOutlineCalendar className="w-4 h-4" />
                  <span>{formatDate(message.created_at)}</span>
                </div>

                {getSecurityBadge(message.security_level)}
              </div>

              {/* Context Info */}
              {(message.case_title || message.client_name || message.session_title) && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase">Related To</p>
                  {message.client_name && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{getEntity('client').singular}:</span> {message.client_name}
                    </p>
                  )}
                  {message.case_title && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{getEntity('case').singular}:</span> {message.case_title}
                    </p>
                  )}
                  {message.session_title && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{getEntity('session').singular}:</span> {message.session_title}
                    </p>
                  )}
                </div>
              )}

              {/* Text Message Content */}
              {message.type === 'text' && message.content && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {message.content}
                  </p>
                </div>
              )}

              {/* Voice Message Player */}
              {message.type === 'voice' && message.voice_stream_url && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 space-y-4">
                  <div className="flex items-center gap-4">
                    {/* Play/Pause Button — gated by stream_voice policy */}
                    <BlockedAction action="stream_voice">
                      <button
                        onClick={handlePlayPause}
                        className="w-14 h-14 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors shadow-lg"
                      >
                        {isPlaying ? (
                          <HiOutlinePause className="w-6 h-6" />
                        ) : (
                          <HiOutlinePlay className="w-6 h-6 ml-1" />
                        )}
                      </button>
                    </BlockedAction>

                    {/* Progress Bar */}
                    <div className="flex-1 space-y-2">
                      <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        value={currentTime}
                        onChange={handleSeek}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${(currentTime / duration) * 100}%, #BFDBFE ${(currentTime / duration) * 100}%, #BFDBFE 100%)`
                        }}
                      />
                      <div className="flex justify-between text-sm text-blue-700 font-medium">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Audio Element */}
                  <audio
                    ref={audioRef}
                    src={message.voice_stream_url}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    className="hidden"
                  />
                </div>
              )}

              {/* Empty State */}
              {message.type === 'text' && !message.content && (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <p className="text-gray-500">No message content</p>
                </div>
              )}

              {message.type === 'voice' && !message.voice_stream_url && (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <p className="text-gray-500">Voice file not available</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              {message.type === 'text' && message.secure_download_url && (
                <BlockedAction action="download">
                  <button
                    onClick={handleDownloadText}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
                  >
                    <HiOutlineArrowDownTray className="w-4 h-4" />
                    Download as .txt
                  </button>
                </BlockedAction>
              )}
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </>
  )
}

export default MessageViewModal
